/* eslint-disable max-len */
/* eslint-disable import/no-unresolved */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Row,
  Col,
  Spin,
  Card,
  Tabs,
  Button,
  message,
} from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import Select from 'components/Select';
import { RFC_PATTERN, CURP_PATTERN, digitoVerificador } from 'utils/patterns';
import FormSubmitControls from 'components/FormSubmitControls';
import { onError } from 'utils/handlers';
import { getCOGInternos } from 'api/catalogos/clasificadoresObjetosGasto';
import { getFormasDePagos } from 'api/catalogos/formasDePagos';
import { formatReceived } from 'utils/formatValues';
import {
  deleteAcreedor,
  getAcreedores,
  patchAcreedor,
  postAcreedor,
} from 'api/configuraciones/acreedores';
import FormItemLabel from 'components/FormItemLabel';
import { getEstadosGlobales } from 'api/catalogos';
import ModalDelete from 'components/ModalDelete';
import Table from 'components/Table';
import AcreedoresDatosDePago from './AcreedoresDatosDePago';
import AcreedoresContactos from './AcreedoresContactos';
import AcreedoresCuentasContables from './AcreedoresCuentasContables';

export const acreedorPermission = {
  permissionModel: 'acreedor',
};

const { TabPane } = Tabs;

// eslint-disable-next-line react/prop-types
const Acreedores = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [clasificadoresDeGasto, setClasificadoresDeGasto] = useState([]);
  const [requiredID, setRequiredID] = useState(false);
  const [requiredRFC, setRequiredRFC] = useState(true);
  const [requiredCURP, setRequiredCURP] = useState(true);
  const [formasDePago, setFormasDePago] = useState([]);
  const [acreedorId, setAcreedorId] = useState();
  const [estadosGlobales, setEstadosGlobales] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    const _data = await getAcreedores();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const [
        _clasificadores,
        _formas_de_pago,
        _estadosGlobales,
        _data,
      ] = await Promise.all([
        getCOGInternos(),
        getFormasDePagos(),
        getEstadosGlobales(),
        getAcreedores(),
      ]);
      if (mounted) {
        setClasificadoresDeGasto(_clasificadores);
        setFormasDePago(_formas_de_pago);
        setEstadosGlobales(_estadosGlobales);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setRequiredRFC(true);
    setRequiredCURP(true);
    setAcreedorId();
    setCurrentTabKey('general');
  };

  const onClickEdit = (responseData = null) => {
    try {
      const [key] = selectedRowKeys;
      const match = responseData || data.find((item) => item.id === key);
      const {
        es_extranjero,
        es_persona_moral,
      } = match;
      setAcreedorId(match.id);
      setRequiredCURP(!es_extranjero && !es_persona_moral);
      setRequiredRFC(!es_extranjero);
      setRequiredID(es_extranjero);
      setTimeout(() => {
        form.setFieldsValue(formatReceived(match));
      });
      setVisible(true);
    } catch (err) {
      onError(err);
    }
  };

  const onFinish = async (_values, _continue = true) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const saved = await (key ? patchAcreedor(key, values) : postAcreedor(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteAcreedor(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Clave',
      key: 'clave',
      dataIndex: 'clave',
      width: 150,
    },
    {
      titleText: 'Tipo Contribuyente',
      key: 'es_persona_moral',
      dataIndex: 'es_persona_moral',
      width: 200,
      render: (val) => (val ? 'Persona Moral' : 'Persona Física'),
    },
    {
      titleText: 'Nacionalidad',
      key: 'es_extranjero',
      dataIndex: 'es_extranjero',
      width: 200,
      render: (val) => (val ? 'Extranjera' : 'Nacional'),
    },
    {
      titleText: 'Nombre o Razón Social',
      key: 'razon_social',
      dataIndex: 'razon_social',
      width: 300,
    },
    {
      titleText: 'RFC',
      key: 'rfc',
      dataIndex: 'rfc',
      width: 250,
    },
    {
      titleText: 'ID Fiscal Extranjero',
      key: 'identificador_fiscal_de_extranjeros',
      dataIndex: 'identificador_fiscal_de_extranjeros',
      width: 250,
    },
    {
      titleText: 'Estado',
      key: 'estados_globales',
      dataIndex: 'estados_globales',
      width: 250,
      render: (val) => estadosGlobales.find((e) => e.id === val)?.descripcion,
    },
  ];

  const rules = {
    required: [
      {
        required: true,
        message: 'El campo es requerido',
      },
    ],
    rfc: [
      {
        required: true,
        message: 'El campo es requerido',
      },
      {
        validator: async (rule, value) => {
          if (requiredRFC) {
            if (value) {
              if (!RFC_PATTERN.test(value)
                || (form.getFieldValue('es_persona_moral') && value.length === 13)
                || (!form.getFieldValue('es_persona_moral') && value.length === 12)) {
                throw new Error('Ingrese un rfc válido');
              }
            }
          }
        },
      },
    ],
    CURP: [
      {
        validator: async (rule, value) => {
          if (requiredCURP) {
            if (value) {
              const validado = value.toUpperCase().match(CURP_PATTERN);
              if (!validado || validado[2] !== digitoVerificador(validado[1])) {
                throw new Error('Ingrese una CURP válida');
              }
            } else {
              throw new Error('El campo es requerido');
            }
          }
        },
      },
    ],
    identificador_fiscal_de_extranjeros: [
      {
        validator: async (rule, value) => {
          if (requiredID && !value) {
            throw new Error('El campo es requerido');
          }
        },
      },
    ],
    correo_electronico: [
      {
        type: 'email',
        message: 'Ingrese un correo electrónico válido',
      },
    ],
    pagina_web: [
      {
        type: 'url',
        message: 'Ingrese una URL válida',
      },
    ],
  };

  const Contribuyente = [
    { label: 'Persona Física', value: false }, // es_persona_moral = false
    { label: 'Persona Moral', value: true }, // es_persona_moral = true
  ];

  const nacionalidadOps = [{ descripcion: 'Nacional', id: false },
    { descripcion: 'Extranjera', id: true }];

  const validateAuthorizable = async (match = {}) => {
    try {
      setLoading(true);
      const errors = [];
      if (!match?.datos_de_pago?.length) {
        errors.push({ message: 'Se necesita mínimo una forma de pago', tab: 'datos_de_pago' });
      }
      if (!match?.clave_de_tercero?.length) {
        errors.push({ message: 'Se necesita mínimo una cuenta contable', tab: 'claves_contables' });
      }
      if (errors.length) {
        message.warn({
          content: (
            <Col>
              <strong>Para autorizar es requerido complete los siguientes datos:</strong>
              <br />
              <br />
              {errors.map((item, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={idx}>
                  <Row justify="space-between">
                    <strong style={{ marginRight: 3 }}>
                      {item.message}
                    </strong>
                  </Row>
                </React.Fragment>
              ))}
              <Button
                icon={<CloseCircleOutlined />}
                onClick={() => message.destroy()}
                type="link"
                style={{
                  position: 'absolute',
                  top: -30,
                  right: -20,
                }}
              />
            </Col>
          ),
          duration: 6,
        });
        setLoading(false);
        return false;
      }
      setLoading(false);
      return true;
    } catch (err) {
      onError(err, setLoading);
      return false;
    }
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <Table
            allowImport
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            permission={permission}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit: () => onClickEdit(),
              onClickDelete,
            }}
            allowExportPDF={false}
          />
        ) : (
          <Card
            className="form-container no-padding"
            title={(
              <FormSubmitControls
                label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Acreedor`}
                onFinish={onFinish}
                validateAuthorizable={validateAuthorizable}
                onCancel={onCancel}
                loading={loading}
                allowAuthorize
                allowCancel
                onFinishText="Guardar y Continuar"
              />
            )}
          >
            <Tabs
              onChange={setCurrentTabKey}
              activeKey={currentTabKey}
              type="card"
            >
              <TabPane
                tab="General"
                key="general"
              >
                <Form
                  layout="vertical"
                  form={form}
                  onFinish={onFinish}
                  scrollToFirstError
                  onValuesChange={(_, allValues) => {
                    const { es_extranjero, es_persona_moral } = allValues;
                    setRequiredCURP(!es_extranjero && !es_persona_moral);
                    setRequiredRFC(!es_extranjero);
                    setRequiredID(es_extranjero);
                  }}
                  initialValues={{
                    es_persona_moral: false,
                    es_extranjero: false,
                    estados_globales: 1,
                  }}
                >
                  <Row gutter={10}>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="clave"
                        rules={rules.required}
                        label="Clave"
                        hasFeedback
                      >
                        <Input allowClear disabled={!selectedRowKeys.length <= 0} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="es_persona_moral"
                        rules={rules.required}
                        label="Tipo de Contribuyente"
                        hasFeedback
                      >
                        <Select labelProp="label" valueProp="value" dataSource={Contribuyente} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="es_extranjero"
                        rules={rules.required}
                        label="Nacionalidad"
                        hasFeedback
                      >
                        <Select dataSource={nacionalidadOps} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="razon_social"
                        rules={rules.required}
                        label="Nombre o Razón Social"
                        hasFeedback
                      >
                        <Input allowClear />
                      </Form.Item>
                    </Col>
                    {requiredRFC && (
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="rfc"
                          rules={rules.rfc}
                          label="RFC"
                          hasFeedback
                          normalize={(val) => val?.toUpperCase()}
                        >
                          <Input maxLength={13} allowClear />
                        </Form.Item>
                      </Col>
                    )}
                    {requiredCURP && (
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="curp"
                          rules={rules.CURP}
                          label="CURP"
                          hasFeedback
                          normalize={(val) => val?.toUpperCase()}
                        >
                          <Input allowClear />
                        </Form.Item>
                      </Col>
                    )}
                    {requiredID && (
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="identificador_fiscal_de_extranjeros"
                          rules={rules.identificador_fiscal_de_extranjeros}
                          label="ID Fiscal Extranjero"
                          hasFeedback
                          normalize={(val) => val?.toUpperCase()}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                  <Row gutter={10}>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="pagina_web"
                        rules={rules.pagina_web}
                        label="Página web"
                        hasFeedback
                      >
                        <Input allowClear />
                      </Form.Item>
                    </Col>
                    {/* <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="clasificador_de_objeto_de_gasto"
                        label={(
                          <FormItemLabel
                            longTitle="Clasificador por Objeto del Gasto"
                            shortTitle="COG"
                            hideOn={['md', 'lg']}
                          />
                        )}
                        hasFeedback
                      >
                        <Select
                          mode="multiple"
                          dataSource={clasificadoresDeGasto}
                          keyLabelRender
                          labelProp="concepto"
                        />
                      </Form.Item>
                    </Col> */}
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="forma_de_pago"
                        rules={rules.required}
                        label="Forma De Pago"
                      >
                        <Select
                          dataSource={formasDePago}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name="estados_globales"
                        label="Estado"
                        rules={rules.required}
                        hasFeedback
                      >
                        <Select
                          disabled={!selectedRowKeys.length}
                          dataSource={estadosGlobales}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                key="datos_pago"
                tab="Datos Pago"
                disabled={!selectedRowKeys.length}
              >
                <AcreedoresDatosDePago
                  acreedorId={acreedorId}
                />
              </TabPane>
              <TabPane
                tab="Contactos"
                key="contactos"
                disabled={!selectedRowKeys.length}
              >
                <AcreedoresContactos
                  acreedorId={acreedorId}
                />
              </TabPane>
              <TabPane
                tab="Cuentas Contables"
                key="claves_contables"
                disabled={!selectedRowKeys.length}
              >
                <Col span={24}>
                  <AcreedoresCuentasContables
                    acreedorId={acreedorId}
                  />
                </Col>
              </TabPane>
            </Tabs>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="El Acreedor"
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default Acreedores;
